import { useState } from "react";
import PageLayout from "@/components/PageLayout";
import { Mail, Phone, MapPin, Clock, MessageSquare, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const CHANNELS = [
  {
    icon: Mail,
    title: "Email Support",
    value: "support@sendora.ng",
    desc: "We reply within 24 hours on business days",
    href: "mailto:support@sendora.ng",
    color: "bg-[#eef1f8] text-[#0c1e41]",
  },
  {
    icon: Phone,
    title: "WhatsApp Support",
    value: "+234 801 234 5678",
    desc: "Mon–Fri, 8 AM–6 PM WAT",
    href: "https://wa.me/2348012345678",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: MapPin,
    title: "Office Address",
    value: "14 Marina Street, Lagos Island",
    desc: "Lagos, Nigeria. By appointment only.",
    href: null,
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: Clock,
    title: "Support Hours",
    value: "Mon–Fri: 8 AM–6 PM WAT",
    desc: "Weekend: 9 AM–1 PM WAT (emergencies only)",
    href: null,
    color: "bg-rose-50 text-rose-600",
  },
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const seoProps = {
    title: "Contact Sendora — Support, Sales & General Enquiries",
    description: "Get in touch with the Sendora team. Email support, WhatsApp, and a contact form. We reply within 24 hours on business days.",
    canonical: "/contact",
    keywords: "contact Sendora, Sendora support, bulk SMS Nigeria support, SMS platform help Nigeria",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "url": "https://www.sendoras.com/contact",
        "name": "Contact Sendora",
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.sendoras.com" },
            { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://www.sendoras.com/contact" }
          ]
        }
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "name": "Sendora Technologies Ltd",
        "url": "https://www.sendoras.com",
        "address": {
          "@type": "PostalAddress",
          "streetAddress": "14 Marina Street, Lagos Island",
          "addressLocality": "Lagos",
          "addressCountry": "NG"
        },
        "email": "support@sendora.ng",
        "telephone": "+2348012345678",
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday"],
            "opens": "08:00",
            "closes": "18:00"
          }
        ]
      }
    ]
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSending(true);
    // Simulate submission
    await new Promise(r => setTimeout(r, 1200));
    setSent(true);
    setSending(false);
    toast.success("Message sent! We'll get back to you within 24 hours.");
  };

  return (
    <PageLayout title="Contact Us" subtitle="Got a question? We're here to help. Reach out via any channel below." seo={seoProps}>
      <section className="py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-10">
            {/* Contact channels */}
            <div className="space-y-5">
              <h2 className="text-xl font-extrabold text-slate-900 mb-6">How to Reach Us</h2>
              {CHANNELS.map(c => (
                <div key={c.title} className="flex gap-4 bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${c.color}`}>
                    <c.icon size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{c.title}</p>
                    {c.href ? (
                      <a href={c.href} target="_blank" rel="noreferrer" className="text-sm font-medium text-[#0c1e41] hover:underline">{c.value}</a>
                    ) : (
                      <p className="text-sm font-medium text-slate-700">{c.value}</p>
                    )}
                    <p className="text-xs text-slate-500 mt-0.5">{c.desc}</p>
                  </div>
                </div>
              ))}

              <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-5 mt-2">
                <p className="text-sm font-semibold text-[#0c1e41] mb-1">For Technical Issues</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  If you're experiencing delivery failures, API errors, or billing issues, please include your account email and the affected message IDs when contacting us. This helps us resolve your issue faster.
                </p>
              </div>
            </div>

            {/* Contact form */}
            <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <h2 className="text-xl font-extrabold text-slate-900 mb-1">Send a Message</h2>
              <p className="text-sm text-slate-500 mb-5">We'll reply within 24 business hours.</p>

              {sent ? (
                <div className="text-center py-10">
                  <div className="w-14 h-14 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Send size={24} className="text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">Message Received!</h3>
                  <p className="text-sm text-slate-500">Thank you for reaching out. Our team will get back to you shortly.</p>
                  <button onClick={() => { setForm({ name: "", email: "", subject: "", message: "" }); setSent(false); }} className="mt-4 text-sm text-[#0c1e41] font-medium hover:underline">
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-700">Full Name *</Label>
                      <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Emeka Okafor" className="h-9 text-sm" data-testid="contact-name" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-slate-700">Email Address *</Label>
                      <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="emeka@company.ng" className="h-9 text-sm" data-testid="contact-email" />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-700">Subject</Label>
                    <Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="e.g. Delivery issue, billing question…" className="h-9 text-sm" data-testid="contact-subject" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-700">Message *</Label>
                    <textarea
                      value={form.message}
                      onChange={e => setForm({ ...form, message: e.target.value })}
                      rows={5}
                      placeholder="Describe your question or issue in detail…"
                      className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-900 bg-white resize-none focus:outline-none focus:border-[#0c1e41] placeholder:text-slate-400"
                      data-testid="contact-message"
                    />
                  </div>
                  <Button type="submit" disabled={sending} className="w-full bg-[#0c1e41] hover:bg-[#091629] shadow-sm" data-testid="contact-submit-btn">
                    {sending ? <Loader2 size={14} className="animate-spin mr-2" /> : <Send size={14} className="mr-2" />}
                    Send Message
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
